// Live report 2026-10-24: a photo taken directly with the phone camera (+ -> camera) showed too
// large and broke the chat layout, while a picture chosen from the gallery was fine. Both go
// through the same AgentChat.applyFile path; the difference is the file itself. A fresh camera
// capture is the full sensor image (typically 4000x3000 px or more, 3-8MB, rotation stored only
// as EXIF metadata), while gallery pictures are usually already resized by the phone or by the
// app they came from. The chat, the vision check and the agent all received the raw capture.
//
// Normalising here - once, before the attachment is created - gives every later step (input
// preview, message thumbnail, lightbox, vision analysis, upload to the agent) the same bounded,
// upright image. Images that are already small (the usual gallery case) are returned untouched,
// and any failure falls back to the original file, so no path that works today can break.
export const MAX_IMAGE_EDGE = 2048;
export const NORMALIZE_ABOVE_BYTES = 1.5 * 1024 * 1024;
const PASS_THROUGH_TYPES = new Set(['image/gif', 'image/svg+xml']);

function outputName(name) {
  const base = String(name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.jpg`;
}

async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* older browsers: fall through */ }
    return await createImageBitmap(file);
  }
  throw new Error('image decoding is not available');
}

/**
 * Returns a File no larger than MAX_IMAGE_EDGE on its longest side, upright, as JPEG - or the
 * original file when it is already small enough, is animated/vector, or cannot be processed.
 */
export async function normalizeImageFile(file, { maxEdge = MAX_IMAGE_EDGE } = {}) {
  if (!file || !String(file.type || '').startsWith('image/') || PASS_THROUGH_TYPES.has(file.type)) return file;
  let bitmap;
  try {
    bitmap = await decode(file);
    const width = bitmap.width;
    const height = bitmap.height;
    const longest = Math.max(width, height);
    if (!longest || (longest <= maxEdge && file.size <= NORMALIZE_ABOVE_BYTES)) return file;
    const scale = Math.min(1, maxEdge / longest);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.fillStyle = '#ffffff'; // JPEG has no transparency
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!blob || !blob.size || (scale === 1 && blob.size >= file.size)) return file;
    return new File([blob], outputName(file.name), { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
  } catch {
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}
